import { CloseOutlined } from '@ant-design/icons';
import { T, useTranslate } from '@tolgee/react';
import {
  Button,
  Col,
  DatePicker,
  Form,
  InputNumber,
  Radio,
  Row,
  Select,
  Space,
} from 'antd';
import TextArea from 'antd/es/input/TextArea';
import { FormInstance } from 'antd/lib/form';
import dayjs from 'dayjs';
import { useMemo, useRef, useState } from 'react';
import calendarIcon from '../../../assets/icons/calendar.svg';
import { WorkforceUnitEnum } from '../../../enums/WorkforceUnitEnum';
import { ISelectedDays } from '../../../utils/calendarUtility';
import { showToast } from '../../../utils/commonUtility';
import { displayCenter } from '../../../utils/styleobjects';
import {
  StaffingStatuses,
  staffingStatusesData,
} from '../calendar-outline/CalendarOutline';
import './AddEventForm.scss';

interface IWorkforceState {
  workforceUnit: WorkforceUnitEnum;
  workforce: number;
}

export const AddEventForm: React.FC<{
  fromDate: Date;
  toDate: Date;
  selectedStaffingDay?: ISelectedDays;
  loading: boolean;
  closeForm: () => any;
  onFormSubmit: (formValues: any) => any;
}> = ({
  fromDate,
  toDate,
  onFormSubmit,
  selectedStaffingDay,
  closeForm,
  loading,
}) => {
  const { t } = useTranslate();
  const [form] = Form.useForm();
  const formRef = useRef<FormInstance>(null);

  const updatedEventRecord = useMemo(
    () => ({
      comments: selectedStaffingDay?.comments || '',
      status: selectedStaffingDay?.status || StaffingStatuses.CLOSE,
      workforce: selectedStaffingDay?.workforce || 0,
      workforceUnit:
        selectedStaffingDay?.workforceUnit || WorkforceUnitEnum.PERCENTAGE,
      id: selectedStaffingDay?.id,
    }),
    [selectedStaffingDay],
  );

  const [fromToDates, setFromToDates] = useState<[Date, Date]>([
    fromDate,
    toDate,
  ]);

  const onFinish = (values: any) => {
    values = {
      ...values,
      startDate: fromToDates[0],
      endDate: fromToDates[1],
    };

    onFormSubmit(values);
  };

  return (
    <div className='addevent-form-container'>
      <Form
        name='add-holiday-form'
        ref={formRef}
        form={form}
        layout='vertical'
        onFinish={onFinish}
        initialValues={updatedEventRecord}
      >
        <div className='title'>
          <img className='title-img' src={calendarIcon} />

          <span>
            <T keyName='nav-calendar' />
          </span>
        </div>
        <div className='closeicon'>
          <CloseOutlined onClick={() => closeForm()} />
        </div>
        <Form.Item name='startDate' style={{ marginBottom: '0' }}>
          <CustomDatePicker
            defaultDate={fromToDates[0]}
            placeholder='DU'
            onDateChange={(fromDate: Date) => {
              setFromToDates(prev => [fromDate, prev[1]]);
            }}
          />
        </Form.Item>

        <Form.Item name='endDate' style={{ marginBottom: '1rem' }}>
          <CustomDatePicker
            defaultDate={fromToDates[1]}
            placeholder='AU'
            onDateChange={(toDate: Date) => {
              setFromToDates(prev => [prev[0], toDate]);
            }}
            disablePrevDates={fromToDates[0]}
          />
        </Form.Item>

        <div style={{ marginBottom: '1rem' }}>
          <Form.Item name='status'>
            <Radio.Group>
              {Object.values(staffingStatusesData).map(
                ({ value, text, className }) => (
                  <div style={{ cursor: 'pointer' }}>
                    <Radio key={value} value={value} className={className}>
                      {t(`${text}`)}
                    </Radio>
                  </div>
                ),
              )}
            </Radio.Group>
          </Form.Item>
        </div>

        <Form.Item dependencies={['status']}>
          {({ getFieldValue }) => {
            const status = getFieldValue('status');
            if (
              ![StaffingStatuses.REDUCTION, StaffingStatuses.INCREASE].includes(
                status,
              )
            ) {
              return null;
            }
            return (
              <Form.Item
                name='workforce'
                label='Workforce'
                rules={[
                  {
                    validator(_, value) {
                      const workforceUnit = getFieldValue('workforceUnit');
                      if (value <= 0)
                        return Promise.reject(
                          new Error(`Workforce can't be zero`),
                        );
                      if (
                        workforceUnit === WorkforceUnitEnum.PERCENTAGE &&
                        value > 100
                      ) {
                        return Promise.reject(
                          new Error(`Workforce percentage can't exceed 100%`),
                        );
                      }
                      if (
                        workforceUnit === WorkforceUnitEnum.FLAT &&
                        /\./.test(String(value))
                      ) {
                        return Promise.reject(
                          new Error(`Decimal value not allowed`),
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
                dependencies={['workforceUnit']}
              >
                <InputNumber
                  min={1}
                  style={{ width: '100%', height: '32px', marginTop: '8px' }}
                  placeholder='Workforce Number'
                  addonAfter={
                    <Form.Item name='workforceUnit' noStyle>
                      <Select style={{ width: 80 }}>
                        <Select.Option value={WorkforceUnitEnum.PERCENTAGE}>
                          %
                        </Select.Option>
                        <Select.Option value={WorkforceUnitEnum.FLAT}>
                          Flat
                        </Select.Option>
                      </Select>
                    </Form.Item>
                  }
                />
              </Form.Item>
            );
          }}
        </Form.Item>

        <Form.Item label={t('Comments')} name='comments'>
          <TextArea
            rows={3}
            // defaultValue={selectedStaffingDay?.comments ?? ''}
            style={{ lineHeight: '1.5rem' }}
            placeholder=''
          />
        </Form.Item>

        <Form.Item style={{ ...displayCenter }}>
          <Button
            type='primary'
            htmlType='submit'
            loading={loading}
            block
            className='submit-btn'
          >
            {loading ? '' : t('submit')}
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
};

export const CustomDatePicker: React.FC<{
  placeholder: string;
  defaultDate?: Date;
  onDateChange: (changedDate: Date) => any;
  disablePrevDates?: Date;
}> = ({
  placeholder,
  defaultDate,
  onDateChange,
  disablePrevDates = new Date(),
}) => {
  const disabledDate = (current: any) => {
    // Disable dates before the specified date
    return current && current.isBefore(disablePrevDates, 'day');
  };
  return (
    <Row align='middle' style={{ marginBottom: '1rem' }}>
      <Col
        style={{
          position: 'absolute',
          zIndex: '9',
          marginLeft: placeholder == 'a' ? '1rem' : '0.3rem',
        }}
      >
        {placeholder && (
          <span
            style={{
              marginRight: '0.8rem',

              fontSize: '1.2rem',
              lineHeight: '3.2rem',
              fontWeight: 'bold',
              color: '#275747',
            }}
          >
            {placeholder + ' '}:
          </span>
        )}
      </Col>
      <Col flex='auto'>
        <DatePicker
          value={dayjs(defaultDate)}
          format='ddd DD/MM/YYYY'
          style={{
            height: '3rem',
            border: 'none',

            paddingLeft: placeholder && '3rem',
            paddingTop: '1.1rem',
            backgroundColor: '#f1f2ed',
          }}
          disabledDate={disabledDate}
          className='custom-date-picker'
          onChange={date => {
            if (!date) return;
            onDateChange(date.toDate());
          }}
          popupClassName='custom-calendar'
        />
      </Col>
    </Row>
  );
};
